// src/PackkySetting/DriverOffersTab.jsx
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

const API = (baseurl) => ({
  insertOffer: `${baseurl}/api/Driver/v1.0/insertDriverOffers`,
  insertIncentive: `${baseurl}/api/Driver/v1.0/insertDriverOffersIncentive`,
  deleteOffer: `${baseurl}/api/Driver/v1.0/deleteDriverOffers`,
  getAll: `${baseurl}/api/Driver/v1.0/getAllDriverOffers`,
});

const clean = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && `${v}`.trim() !== "")
  );

export default function DriverOffersTab() {
  const { baseurl, token, accessToken, setIsUnauthorised } = useGlobalContext();

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOfferId, setExpandedOfferId] = useState(null);

  // trigger refetch
  const [refreshKey, setRefreshKey] = useState(0);

  // Create Offer
  const [title, setTitle] = useState("");
  const [totalEarnings, setTotalEarnings] = useState("");
  const [validity, setValidity] = useState("");

  // Incentive (per-offer)
  const [gigsLevel, setGigsLevel] = useState("");
  const [orders, setOrders] = useState("");
  const [earnings, setEarnings] = useState("");
  const [incentive, setIncentive] = useState("");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Basic ${token}`,
      "Content-Type": "application/json",
      ...(accessToken ? { "x-access-token": accessToken } : {}),
    }),
    [token, accessToken]
  );
  const endpoints = useMemo(() => API(baseurl), [baseurl]);

  // ---------------- Get All Driver Offers (userid empty by default) -------------
  const fetchOffers = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const payload = { userid: "" }; // ← per your instruction
      const res = await axios.post(endpoints.getAll, payload, { headers: authHeaders });

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.response)
          ? res.data.response
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        setOffers(list);
      } else if (res?.data?.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to fetch Driver Offers");
      }
    } catch {
      toast.error("Error fetching Driver Offers");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers(); // auto-fetch on mount & when refreshKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, baseurl, token, accessToken]);

  // ---------------- Insert Offer (NO offerId) -----------------------------------
  const handleCreateOffer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = clean({ title, totalEarnings, validity });
      const res = await axios.post(endpoints.insertOffer, body, { headers: authHeaders });

      if (res?.data?.responseCode === "00") {
        toast.success("Offer created");
        setTitle("");
        setTotalEarnings("");
        setValidity("");
        setRefreshKey((k) => k + 1);
      } else if (res?.data?.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to create offer");
      }
    } catch {
      toast.error("Error creating offer");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Delete Offer ------------------------------------------------
  const handleDeleteOffer = async (offer) => {
    const offerId =
      offer?.offerid || offer?.offerId || offer?.pk_offer_id || offer?.id || offer?.pk_id;

    if (!offerId) return toast.error("Offer ID missing");

    setLoading(true);
    try {
      const res = await axios.post(
        endpoints.deleteOffer,
        { offerid: `${offerId}` }, // API expects lowercase `offerid`
        { headers: authHeaders }
      );

      if (res?.data?.responseCode === "00") {
        toast.success("Offer deleted");
        setRefreshKey((k) => k + 1);
      } else if (res?.data?.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to delete offer");
      }
    } catch {
      toast.error("Error deleting offer");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Insert Incentive (NO incentiveid; MUST send offerId) -------
  const handleAddIncentive = async (offer) => {
    const offerId =
      offer?.offerId || offer?.offerid || offer?.pk_offer_id || offer?.id || offer?.pk_id;

    if (!offerId) return toast.error("Offer ID missing");

    setLoading(true);
    try {
      const body = clean({
        offerId: `${offerId}`,
        gigsLevel,
        orders,
        earnings,
        incentive,
      });

      const res = await axios.post(endpoints.insertIncentive, body, { headers: authHeaders });

      if (res?.data?.responseCode === "00") {
        toast.success("Incentive added");
        setGigsLevel("");
        setOrders("");
        setEarnings("");
        setIncentive("");
        setExpandedOfferId(null);
        setRefreshKey((k) => k + 1);
      } else if (res?.data?.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res?.data?.responseMessage || "Failed to add incentive");
      }
    } catch {
      toast.error("Error adding incentive");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------- UI -----------------------------------------
  return (
    <div className="admin-driver-offers p-2 space-y-6">
      {/* Top bar: only Refresh & loader (UserID field removed) */}
      <div className="admin-bar flex items-center gap-3">
        <button
          onClick={() => fetchOffers(true)}
          className="admin-refresh-btn inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          Refresh
        </button>
        {loading && <span className="text-xs text-gray-500">Loading…</span>}
      </div>

      {/* Create Offer */}
      <div className="admin-card bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">Create Driver Offer</h3>
        </div>
        <form onSubmit={handleCreateOffer} className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="admin-field">
            <label className="block text-xs text-gray-500 mb-1">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Festive Gigs Booster"
              required
            />
          </div>

          <div className="admin-field">
            <label className="block text-xs text-gray-500 mb-1">Total Earnings</label>
            <input
              value={totalEarnings}
              onChange={(e) => setTotalEarnings(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 1500"
              required
            />
          </div>

          <div className="admin-field">
            <label className="block text-xs text-gray-500 mb-1">Validity</label>
            <input
              value={validity}
              onChange={(e) => setValidity(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 2025-12-31 or 'Till Month End'"
              required
            />
          </div>

          <div className="md:col-span-3">
            <button
              type="submit"
              className="admin-primary-btn inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              Create Offer
            </button>
          </div>
        </form>
      </div>

      {/* Offers List */}
      <div className="admin-list bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">All Driver Offers</h3>
        </div>

        <div className="p-2">
          {(!offers || offers.length === 0) && !loading && (
            <div className="text-sm text-gray-500 px-2 py-3">No offers found. Create one above.</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {offers?.map((o, idx) => {
              const offerId =
                o?.offerId || o?.offerid || o?.pk_offer_id || o?.id || `offer-${idx}`;
              const _title = o?.title || o?.offer_title || "Untitled Offer";
              const _total = o?.totalEarnings || o?.total_earnings || o?.earnings || "-";
              const _validity = o?.validity || o?.valid_till || "-";
              const expanded = expandedOfferId === offerId;

              return (
                <div
                  key={`${offerId}-${idx}`}
                  className="admin-offer-card rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                    <div>
                      <div className="text-sm font-semibold">{_title}</div>
                      <div className="text-xs text-gray-500">Offer ID: {offerId}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpandedOfferId(expanded ? null : offerId)}
                        className="px-3 py-1.5 rounded-md text-xs bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {expanded ? "Close" : "Add Incentive"}
                      </button>
                      <button
                        onClick={() => handleDeleteOffer(o)}
                        className="px-3 py-1.5 rounded-md text-xs bg-red-600 text-white hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="px-4 py-3 text-sm">
                    <div className="text-gray-600">
                      <div><span className="text-gray-500">Total Earnings:</span> {_total}</div>
                      <div><span className="text-gray-500">Validity:</span> {_validity}</div>
                    </div>
                  </div>

                  {/* Incentive form */}
                  {expanded && (
                    <div className="border-t px-4 py-3 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Gigs Level</label>
                          <input
                            value={gigsLevel}
                            onChange={(e) => setGigsLevel(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Bronze / 1 / L1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Orders</label>
                          <input
                            value={orders}
                            onChange={(e) => setOrders(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 25"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Earnings</label>
                          <input
                            value={earnings}
                            onChange={(e) => setEarnings(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Incentive</label>
                          <input
                            value={incentive}
                            onChange={(e) => setIncentive(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., +5%"
                          />
                        </div>
                      </div>

                      <div className="mt-3">
                        <button
                          onClick={() => handleAddIncentive(o)}
                          className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
                        >
                          Save Incentive
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
